import toNumber from 'lodash/toNumber'
import { gql } from '@apollo/client'
import { type EntityWithSelection, useMutation } from '@ztechapps/common.hook'
import type {
  CreateAndBuildWorkOrderArgs,
  ProductBillOfMaterial,
  UnitOfMeasure,
  WorkOrder,
} from '@ztechapps/common.contract'
import { filterTree, mapTree } from '@ztechapps/common.utils'

export const createAndBuildWorkOrderQuery = gql(`
  mutation createAndBuildWorkOrder($input: CreateAndBuildWorkOrderArgs) {
    createAndBuildWorkOrder(input: $input) {
      _id
      code
      status
    }
  }
`)

export type UseCreateAndBuildWorkOrderResult = [
  (formValues: CreateAndBuildWorkOrderArgs) => void,
  { loading: boolean; error: unknown },
]

export const useCreateAndBuildWorkOrder = (): UseCreateAndBuildWorkOrderResult => {
  const [createAndBuildWorkOrder, , { loading, error }] = useMutation<WorkOrder, CreateAndBuildWorkOrderArgs>({
    query: createAndBuildWorkOrderQuery,
    options: {
      mutationOptions: {
        refetchQueries: [
          'singleOrder',
          'allProduct',
          'singleProduct',
          'allInventoryTimeSeries',
          'allInventoryBySerialNumber',
        ],
      },
    },
  })

  const onAction = async ({
    remarks,
    product,
    quantity,
    tags,
    relatedSalesOrders,
    bomItems,
  }: CreateAndBuildWorkOrderArgs) => {
    quantity = toNumber(quantity)

    const buildWorkOrderItems = () => {
      const CHILDREN_NAME = 'product.billOfMaterials'

      const rootBom = {
        product: {
          ...product,
          billOfMaterials: bomItems,
        },
        _selected: true,
      } as EntityWithSelection<ProductBillOfMaterial>

      const selectedItemsOnly = filterTree(rootBom, (node) => node._selected, {
        childrenName: CHILDREN_NAME,
        startsFromRoot: false,
      })

      //re-map the fields to remove the _selected, and set its value to requiredToBuild
      //we need this because server computation includes only the required bom
      const neededFieldsOnly = mapTree<ProductBillOfMaterial>(
        selectedItemsOnly,
        (e) => {
          const { _selected, ...rest } = e as EntityWithSelection<ProductBillOfMaterial>

          // Ensure nested UOM objects follow UnitOfMeasureInput type
          const p = rest.product
          if (p) {
            p.baseUom = toUomInput(p.baseUom)
            p.workOrderUom = toUomInput(p.workOrderUom)
          }

          return {
            ...rest,
            requiredToBuild: _selected,
          } as ProductBillOfMaterial
        },
        { childrenName: CHILDREN_NAME }
      )

      return neededFieldsOnly?.product?.billOfMaterials
    }

    // Build input following CreateAndBuildWorkOrderArgs type
    const input: CreateAndBuildWorkOrderArgs = {
      remarks: remarks ?? '',
      bomItems: buildWorkOrderItems(),
      product: { _id: product?._id },
      quantity,
      tags: tags ?? [],
      relatedSalesOrders: relatedSalesOrders ?? [],
    }

    await createAndBuildWorkOrder({ input })
  }

  return [onAction, { loading, error }]
}

/** Pick only fields defined in UnitOfMeasureInput */
const toUomInput = (uom: UnitOfMeasure): UnitOfMeasure => {
  if (!uom) return uom
  return {
    _id: uom._id,
    code: uom.code,
    name: uom.name,
    symbol: uom.symbol,
    category: uom.category,
    conversionFactor: uom.conversionFactor,
    status: uom.status,
  }
}
